import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import * as Notation from '../notation';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import type { LetterPanelOptions } from '../uTheory.Notation/letterPanel';

export type GenericIntervalIDQuestion = {
  question: {
    note: Notation.Note;
    note2: Notation.Note;
    interval: Notation.Interval;
    clef: ClefName;
  };
  answer: string;
  text: string;
  skills: (number | string)[];
};

interface GenericIntervalIDOptions extends InteractiveOptions {
  lowestNote?: string | Notation.Note;
  highestNote?: string | Notation.Note;
  halfWholeOnly?: boolean;
}

class GenericIntervalID extends InteractivePrototype {
  skillCalculator!: SkillCalculator;

  maxQuestions!: number;

  clef!: Notation.Clef;

  note1Holder!: Notation.NoteHolder;

  note2Holder!: Notation.NoteHolder;

  question: GenericIntervalIDQuestion | undefined;

  declare questions: GenericIntervalIDQuestion[];

  declare currentQuestion: GenericIntervalIDQuestion;

  declare options: GenericIntervalIDOptions;

  interval: Notation.Interval | undefined;

  note: Notation.Note | undefined;

  note2: Notation.Note | undefined;

  answer: string | undefined;

  answerAttempts: number;

  constructor(displayDiv: HTMLDivElement, options: GenericIntervalIDOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = ['generic_intervals'];
    this.settings = {
      instructions: true,
      notation: {
        fixedWidth: 150,
        ariaTitle: {
          clef: false,
          keySignature: false,
          rhythm: false,
        },
      },
      letters: {
        numberButtons: true,
        checkButton: false,
      },
      checkMark: {
        rightAnswerLength: 0.6,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };
    this.layout = {
      instructions: {
        maxPercent: 40,
        noWrap: true,
      },
    };
    this.answerAttempts = 0;

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: GenericIntervalIDOptions): Promise<void> {
    if (options.halfWholeOnly === true) {
      const letters = this.settings.letters as Partial<LetterPanelOptions>;

      letters.numberButtons = false;
      letters.intervalQualityButtons = false;
      letters.checkButton = false;

      letters.customButtons = [
        { text: 'CHS', value: 'A1' },
        { text: 'DHS', value: 'm2' },
        { text: 'WS', value: 'M2' },
        { text: 'Other', value: 'Other' },
      ];

      options.skills = ['chs', 'dhs', 'ws'];
    }

    if (options.lowestNote === undefined) {
      options.lowestNote = 'C2';
    }

    if (options.highestNote === undefined) {
      options.highestNote = 'C6';
    }

    // GCR TODO: If not, initialize others.

    // now call the prototype's init function:

    this.skillCalculator = new SkillCalculator();
    return super.init(displayDiv, options);
  }

  generateQuestions(): void {
    const options = this.options;
    const drill = options.drill !== undefined && options.drill === true;
    this.questions = drill
      ? this.generateDrillQuestions(options)
      : this.generateManualQuestions(options);

    this.maxQuestions = this.questions.length;
  }

  generateDrillQuestions(options: GenericIntervalIDOptions): GenericIntervalIDQuestion[] {
    const { lowestNote, highestNote } = options;
    const generator = new QuestionGenerator();
    const questions = generator.generate(options.skills, options.maxQuestions || 10, {
      lowestNote,
      highestNote,
    });

    return this.processQuestions(questions);
  }

  generateManualQuestions(options: GenericIntervalIDOptions): GenericIntervalIDQuestion[] {
    const questions = (options.questions || []).map((question) => {
      const questionParts = question.split('/');
      const note = new Notation.Note(questionParts[0]);
      const interval = new Notation.Interval(questionParts[1]);

      return { note, interval };
    });

    return this.processQuestions(questions);
  }

  processQuestions(
    questions: Pick<GenericIntervalIDQuestion['question'], 'note' | 'interval'>[]
  ): GenericIntervalIDQuestion[] {
    return questions.map((question, index) => {
      const { note, interval } = question;
      const note2 = note.getNoteAt(interval) as Notation.Note;
      // const skills = this.calculateSkill(interval);
      const skills = this.calculateSkill();
      const clef = this.getClefByNotes(note, note2);

      return {
        question: {
          note,
          note2,
          interval,
          clef,
        },
        answer: `${interval.genericInterval}`,
        text: `${index + 1}`,
        skills: [skills],
      };
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getClefByNotes(note: Notation.Note, note2: Notation.Note): ClefName {
    if (note.isGreaterThan('F4') || note2.isGreaterThan('F4')) {
      return 'treble';
    }
    if (note.isLessThan('G3') || note2.isLessThan('G3')) {
      return 'bass';
    }
    return Math.random() > 0.5 ? 'treble' : 'bass';
  }

  calculateSkill() {
    return 'generic_intervals';
  }

  initializeMusic(): void {
    const clef = new Notation.Clef('treble');
    const staff = new Notation.Staff(clef);

    this.clef = clef;
    this.note1Holder = new Notation.NoteHolder(new Notation.Note(), 'h');
    this.note2Holder = new Notation.NoteHolder(new Notation.Note(), 'h');

    const measure = new Notation.Measure(this.note1Holder, this.note2Holder);
    const voice = new Notation.Voice(measure);
    voice.setStaff(staff);
    const keySig = new Notation.KeySignature('C', 'Major');

    this.music = new Notation.Music(staff, voice, keySig);
  }

  newQuestion(): void {
    if (this.progressTracker) {
      this.progressTracker.start(this.targetSpeed);
    }

    const question = this.questions[this.questionNumber] as GenericIntervalIDQuestion;
    const interval = question.question.interval;
    const note = question.question.note;
    const note2 = question.question.note2;
    const clef = question.question.clef;

    this.questionPrompt = this.getQuestionPrompt();
    this.question = question;
    this.interval = interval;
    this.note = note;
    this.note2 = note2;

    this.note1Holder.notes[0] = note;
    this.note2Holder.notes[0] = note2;
    this.clef.setName(clef);
    this.notation!.render();

    this.wrongAnswerCount = 0;
    super.newQuestion();
  }

  getQuestionPrompt(): string {
    let questionPrompt;
    const optionsQuestionPrompt = this.options.questionPrompt;
    const testMode = this.optionsTrue('testMode');

    if (testMode === true) {
      questionPrompt = 'Identify generic interval you see';
    } else if (testMode === false) {
      questionPrompt = `${optionsQuestionPrompt}<br />#{question} of ${this.questions.length}`;
    } else {
      questionPrompt = `Identify generic interval you see.`;
    }

    return questionPrompt;
  }

  newCheckpointQuestion(questionString: string): Promise<GenericIntervalIDQuestion> {
    return this.loadingPromise.then(() => {
      const questionParts = questionString.split('/');
      const note = new Notation.Note(questionParts[0] as NoteName);
      const interval = new Notation.Interval(questionParts[1] as IntervalName);
      const note2 = note.getNoteAt(interval) as Notation.Note;
      // const skills = this.calculateSkill(interval);
      const skills = this.calculateSkill();
      const clef = this.getClefByNotes(note, note2);
      const questions = [];

      questions.push({
        question: {
          note,
          note2,
          interval,
          clef,
        },
        answer: `${interval.genericInterval}`,
        skills: [skills],
        text: '0',
      });

      this.questions = questions;

      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners(): void {
    this.letterPanel!.addNumberCallback(this.checkAnswer, this);
  }

  clearAnswer(): void {
    this.answer = '';
  }

  checkAnswer(answer: string | number): void {
    if (this.rightAnswerDelay) return;
    this.answerAttempts++;
    if (typeof answer === 'number') {
      answer = `${answer}`;
    }
    this.answer = answer;

    const testMode = this.optionsTrue('testMode');
    const questionAnswer = this.questions[this.questionNumber]!.answer;
    const userAnswer = answer;
    const correct = questionAnswer === userAnswer;

    if (testMode === true) {
      this.checkpointAnswer(correct);
      this.clearAnswer();
      return;
    }

    if (correct === true) {
      this.getAnswerAndData(true);
      this.rightAnswer(1200);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  getAnswerAndData(correct: boolean): AnswerAndData {
    const correctAnswer = this.questions[this.questionNumber]!.answer;
    const currentQuestion = this.questions[this.questionNumber] as GenericIntervalIDQuestion;
    const question = `${currentQuestion.question.note}/${currentQuestion.question.interval}`;
    const answerAndData = {
      question,
      questionType: 'GenericIntervalID',
      questionText:
        this.questionPrompt && typeof this.questionPrompt === 'string' ? this.questionPrompt : '',
      questionSVG: this.notation!.exportSVG(),
      studentAnswerText: `${this.answer}`,
      correctAnswerText: `${correctAnswer}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.questions[this.questionNumber]!.skills,
    };

    return answerAndData;
  }

  gameOver(): void {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv!);

    super.gameOver();
  }

  playAnswer(): void {
    if (this.note && this.note2) {
      this.player!.play(this.note.getMidiNumber(), { startTime: 0, stopTime: 0.6 });
      this.player!.play(this.note2.getMidiNumber(), { startTime: 0.5, stopTime: 1.5 });
    }
  }
}

export default GenericIntervalID;
